// Savings goals + contributions, persisted on device.

import { create } from 'zustand';

import { getDb, TABLES } from '@/db';
import {
  cancelGoalIdleReminder,
  notifyGoalMilestone,
  scheduleGoalIdleReminder,
} from '@/notifications/local';
import { scheduleHouseholdSync } from '@/sync/household-sync';
import type { GoalContribution, SavingsGoal } from '@/types/domain';
import { formatPln, todayIso, uuid } from '@/utils/format';
import { milestoneFor } from '@/utils/goal-milestones';

export interface NewGoalInput {
  name: string;
  template: SavingsGoal['template'];
  targetGr: number;
  startingGr: number;
  targetDate: string | null;
  icon?: string | null;
}

/** Fields the edit screen may change; anything else stays immutable. */
export type GoalPatch = Partial<Pick<SavingsGoal, 'name' | 'targetGr' | 'targetDate' | 'icon'>>;

// Derived values are PURE functions over subscribed state — never store
// getters. React Compiler memoizes calls to stable store-method references,
// so `store.someGetter()` in render can return stale data forever.
export function savedGrOf(goal: SavingsGoal, contributions: GoalContribution[]): number {
  return (
    goal.startingGr +
    contributions.filter((c) => c.goalId === goal.id).reduce((sum, c) => sum + c.amountGr, 0)
  );
}

export function totalSavingsGrOf(goals: SavingsGoal[], contributions: GoalContribution[]): number {
  return goals.reduce((sum, g) => sum + savedGrOf(g, contributions), 0);
}

interface GoalsState {
  hydrated: boolean;
  goals: SavingsGoal[];
  /** newest first */
  contributions: GoalContribution[];
  hydrate: () => Promise<void>;
  addGoal: (input: NewGoalInput) => Promise<SavingsGoal>;
  updateGoal: (goalId: string, patch: GoalPatch) => Promise<void>;
  deleteGoal: (goalId: string) => Promise<void>;
  addContribution: (goalId: string, amountGr: number, memberId: string) => Promise<void>;
}

export const useGoalsStore = create<GoalsState>((set) => ({
  hydrated: false,
  goals: [],
  contributions: [],

  hydrate: async () => {
    const db = await getDb();
    const goals = (await db.list<SavingsGoal>(TABLES.goals)).sort((a, b) =>
      a.createdAt.localeCompare(b.createdAt),
    );
    const contributions = (await db.list<GoalContribution>(TABLES.contributions)).sort((a, b) =>
      b.createdAt.localeCompare(a.createdAt),
    );
    set({ hydrated: true, goals, contributions });
  },

  addGoal: async (input) => {
    const now = new Date().toISOString();
    const goal: SavingsGoal = {
      id: uuid(),
      householdId: 'hh-0001',
      name: input.name,
      template: input.template,
      targetGr: input.targetGr,
      startingGr: input.startingGr,
      icon: input.icon ?? null,
      photoUri: null,
      targetDate: input.targetDate,
      createdAt: now,
      updatedAt: now,
    };
    const db = await getDb();
    await db.upsert(TABLES.goals, goal.id, goal);
    set((s) => ({ goals: [...s.goals, goal] }));
    // Start the 30-days-of-silence clock right away (native only, web no-op).
    void scheduleGoalIdleReminder(goal.id, goal.name).catch(() => undefined);
    scheduleHouseholdSync();
    return goal;
  },

  updateGoal: async (goalId, patch) => {
    const current = useGoalsStore.getState().goals.find((g) => g.id === goalId);
    if (!current) return;
    const updated: SavingsGoal = { ...current, ...patch, updatedAt: new Date().toISOString() };
    const db = await getDb();
    await db.upsert(TABLES.goals, goalId, updated);
    set((s) => ({ goals: s.goals.map((g) => (g.id === goalId ? updated : g)) }));
    scheduleHouseholdSync();
  },

  deleteGoal: async (goalId) => {
    const db = await getDb();
    // Remove the goal's contributions too — orphaned rows would still count
    // into totalSavingsGrOf via nothing (filtered by goalId) but bloat the db.
    const doomed = useGoalsStore.getState().contributions.filter((c) => c.goalId === goalId);
    for (const c of doomed) await db.remove(TABLES.contributions, c.id);
    await db.remove(TABLES.goals, goalId);
    set((s) => ({
      goals: s.goals.filter((g) => g.id !== goalId),
      contributions: s.contributions.filter((c) => c.goalId !== goalId),
    }));
    void cancelGoalIdleReminder(goalId);
    scheduleHouseholdSync();
  },

  addContribution: async (goalId, amountGr, memberId) => {
    const now = new Date().toISOString();
    const contribution: GoalContribution = {
      id: uuid(),
      goalId,
      amountGr,
      memberId,
      date: todayIso(),
      createdAt: now,
    };
    const state = useGoalsStore.getState();
    const goal = state.goals.find((g) => g.id === goalId);
    const savedBefore = goal ? savedGrOf(goal, state.contributions) : 0;

    const db = await getDb();
    await db.upsert(TABLES.contributions, contribution.id, contribution);
    set((s) => ({ contributions: [contribution, ...s.contributions] }));
    scheduleHouseholdSync();

    if (goal) {
      // Fresh money -> restart the 30-days-of-silence clock.
      void scheduleGoalIdleReminder(goal.id, goal.name).catch(() => undefined);
      // Milestone push exactly once per crossing: fire only if this very
      // contribution pushed savings over a milestone line ([] = ignore card
      // dismissals — a push celebrates the crossing, the card has its own X).
      const before = milestoneFor(goal.targetGr, savedBefore, []);
      const after = milestoneFor(goal.targetGr, savedBefore + amountGr, []);
      if (after !== null && after !== before) {
        void notifyGoalMilestone(goal.name, formatPln(savedBefore + amountGr)).catch(() => undefined);
      }
    }
  },
}));
